import type { TAnalysisResultValue } from '@team-milva/lab-models/resources'

type TQaConfig = {
  type: string
  qaTrigger?: string | null
  qaAnswerIDs?: string[]
}

/**
 * Whether an entered result value matches the analysis test's QA setting — the browser's copy of
 * lab-cloud's `doesResultTriggerQa`, so the result-entry modal flags "QA påkrævet" for exactly what
 * the backend will then insist on. Keep the two in step.
 *
 * - prove-based: `always` / `proved` / `not-proved`; an unanswered result never triggers.
 * - single-select (MIL-551): `always`, or `answers` — the chosen answer is one of `qaAnswerIDs`
 *   (also when it has since been deactivated); no answer chosen never triggers.
 */
export function doesResultTriggerQa(
  analysisTest: TQaConfig | undefined,
  value: TAnalysisResultValue | null | undefined
): boolean {
  if (!analysisTest || !value) return false

  if (analysisTest.type === 'prove-based') {
    if (value.type !== 'prove-based' || value.isProved === null) return false
    switch (analysisTest.qaTrigger) {
      case 'always':
        return true
      case 'proved':
        return value.isProved === true
      case 'not-proved':
        return value.isProved === false
      default:
        return false
    }
  }

  if (analysisTest.type === 'single-select') {
    if (value.type !== 'single-select' || value.answerID === null) return false
    switch (analysisTest.qaTrigger) {
      case 'always':
        return true
      case 'answers':
        return (analysisTest.qaAnswerIDs ?? []).includes(value.answerID)
      default:
        return false
    }
  }

  return false
}
